import { Component, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { COMMON_IMPORTS } from '../../../../Shared/common-imports';
import { AvContentContainerComponent } from '../../../../Shared/Components/av-content-container/av-content-container.component';
import { MsgService } from '../../../../Shared/Services/msg.service';
import { LicencesFacade } from '../../../../../Facades/licences.facade';
import { Licence, LicenceComponentSummary } from '../../../../../Interfaces/api/licences.interface';
import { LicenceAssignDialogData } from '../../../Interfaces/licence-assign-dialog.interface';
import { LicenceQuantityDialogData } from '../../../Interfaces/licence-quantity-dialog.interface';
import { LicenceQuantityMode } from '../../../Types/licence-quantity-mode.type';
import { LicenceComponentCardComponent } from './Partials/licence-component-card/licence-component-card.component';
import { LicenceRosterTableComponent } from './Partials/licence-roster-table/licence-roster-table.component';
import { LicenceQuantityDialogComponent } from './Dialogs/licence-quantity-dialog/licence-quantity-dialog.component';
import { LicenceAssignDialogComponent } from './Dialogs/licence-assign-dialog/licence-assign-dialog.component';

@Component({
    selector: 'app-licences',
    standalone: true,
    imports: [
        COMMON_IMPORTS,
        AvContentContainerComponent,
        LicenceComponentCardComponent,
        LicenceRosterTableComponent,
    ],
    templateUrl: './licences.component.html',
    styleUrls: ['../billing/billing-page.scss', '../billing/billing.scss', './licences.scss'],
})
export class LicencesComponent {
    readonly facade = inject(LicencesFacade);
    private readonly _dialog = inject(MatDialog);
    private readonly _msgService = inject(MsgService);
    private readonly _translate = inject(TranslateService);

    // Purchase

    async buyLicences(component: LicenceComponentSummary): Promise<void> {
        const quantity = await this.promptQuantity('buy', component);

        if (!quantity) {
            return;
        }

        try {
            await firstValueFrom(this.facade.buy(quantity, component.component_type));
            this.facade.reload();
            this._msgService.showSnackBar('app.billing.licences.messages.purchased');
        } catch (error) {
            this.showFailure(error);
        }
    }

    // Cancellation

    async cancelLicences(component: LicenceComponentSummary): Promise<void> {
        const quantity = await this.promptQuantity('cancel', component);

        if (!quantity) {
            return;
        }

        try {
            await firstValueFrom(this.facade.cancel(quantity, component.component_type));
            this.facade.reload();
            this._msgService.showSnackBar('app.billing.licences.messages.canceled');
        } catch (error) {
            this.showFailure(error);
        }
    }

    async resumeLicence(licence: Licence): Promise<void> {
        try {
            await firstValueFrom(this.facade.resume(licence.id));
            this.facade.reload();
            this._msgService.showSnackBar('app.billing.licences.messages.resumed');
        } catch (error) {
            this.showFailure(error);
        }
    }

    // Assignment

    async assignLicence(licence: Licence): Promise<void> {
        const userId = await firstValueFrom(
            this._dialog
                .open<LicenceAssignDialogComponent, LicenceAssignDialogData, string>(LicenceAssignDialogComponent, {
                    data: { licence, users: this.facade.assignableUsers() },
                    panelClass: 'av-dialog',
                })
                .afterClosed(),
        );

        if (!userId) {
            return;
        }

        try {
            const response = await firstValueFrom(this.facade.assign(licence.id, userId));
            this.facade.reload();
            this._msgService.showSnackBar('app.billing.licences.messages.assigned');
            this.warnWhenPendingCancellation(response.data.cancels_at);
        } catch (error) {
            this.showFailure(error);
        }
    }

    async releaseLicence(licence: Licence): Promise<void> {
        const confirmed = await firstValueFrom(
            this._msgService
                .showConfirm(
                    'app.billing.licences.release.title',
                    'app.billing.licences.release.confirm_body',
                    '',
                    'person_remove',
                    'error',
                )
                .afterClosed(),
        );

        if (!confirmed) {
            return;
        }

        try {
            await firstValueFrom(this.facade.release(licence.id));
            this.facade.reload();
            this._msgService.showSnackBar('app.billing.licences.messages.released');
        } catch (error) {
            this.showFailure(error);
        }
    }

    // Shared

    private promptQuantity(
        mode: LicenceQuantityMode,
        component: LicenceComponentSummary,
    ): Promise<number | undefined> {
        return firstValueFrom(
            this._dialog
                .open<LicenceQuantityDialogComponent, LicenceQuantityDialogData, number>(
                    LicenceQuantityDialogComponent,
                    {
                        data: { mode, component },
                        panelClass: 'av-dialog',
                    },
                )
                .afterClosed(),
        );
    }

    private warnWhenPendingCancellation(cancelsAt: string | null): void {
        if (!cancelsAt) {
            return;
        }

        this._msgService.showAlert(
            'app.billing.licences.assign.pending_warning_title',
            this._translate.instant('app.billing.licences.assign.pending_warning', {
                date: new Date(cancelsAt).toLocaleDateString(this._translate.currentLang),
            }),
            'schedule',
        );
    }

    private showFailure(error: unknown): void {
        const body = (error as { error?: { message?: string } })?.error;
        this._msgService.showSnackBar(body?.message ?? 'shared.general.errors.default', { type: 'error' });
    }
}
